/* eslint-disable @typescript-eslint/no-unused-vars */
import { useNotification } from "@refinedev/core";
import { Form, FormProps, Input, Modal, ModalProps } from "antd";
import isEqual from "lodash.isequal";
import { useCallback, useEffect, useState } from "react";
import {
  IWalletBody,
  useDeleteWallet,
  useGetDetailWallet,
  useUpdateWallet,
} from "../../../api/wallet";
import { AutoComplete } from "../../../components/inputs/autocomplete";
import {
  AutoCompleteCurrencyToken,
  Option,
} from "../../../components/inputs/autocomplete/autocompleteCurrencyToken";
import { AutoCompleteNetwork } from "../../../components/inputs/autocomplete/autocompleteNetwork";
import { ICompany } from "../../../interfaces/companies";
import { ICurrencyToken } from "../../../interfaces/currencyToken";
import { INetwork } from "../../../interfaces/network";
import { ButtonListFooterWithDeleteModalSM } from "../../../pages/components/buttonsList";
import {
  confirmModalDeleteProps,
  confirmModalUnsavedChangesProps,
} from "../../../pages/components/constants";
import { addressWalletRules } from "../../../pages/payment-request/addCompany.constants";
import {
  mapCompanyItem,
  mapCurrencyToken,
  mapNetwork,
} from "../../../utils/edit.constants";
import { companySearchFieldNameFilter } from "../../../utils/form.constants";
import { useNotificationOkCancel } from "../../../utils/hooks/useNotificationOkCancel.hook";
import { useProviderWallet } from "../../../utils/hooks/useProviderWallet.hook";
import { DownOutlined } from "@ant-design/icons";

interface IProps {
  formProps: FormProps;
  modalProps?: ModalProps;
  closeModal: () => void;
  editingId?: number;
  changeEditingId: (id: number | undefined) => void;
  refetchList: () => void;
  show: () => void;
  userId?: number;
}

export const EditWalletModal = ({
  modalProps,
  formProps,
  editingId,
  changeEditingId,
  closeModal,
  refetchList,
  show,
  userId,
}: IProps) => {
  const { data, isLoading: loadIsLoadingDetail } = useGetDetailWallet(
    editingId || 0
  );
  const profileDetailString = localStorage.getItem("refine-user-details");
  const profileDetail = profileDetailString
    ? JSON.parse(profileDetailString)
    : null;
  const [currencyData, setCurrencyData] = useState<ICurrencyToken>();
  const [networkData, setNetworkData] = useState<INetwork>();
  const [companyData, setCompanyData] = useState<ICompany>();
  const { data: loadData, isLoading: loadIsLoading } = useProviderWallet(
    data?.data?.data
  );
  const { open: openNotificationWallet } = useNotification();

  const { mutate: updateWallet, isLoading: isLoadingUpdate } = useUpdateWallet({
    onSuccess: (response: { data: { message: string } }) => {
      if (openNotificationWallet) {
        openNotificationWallet?.({
          key: "success_transaction_confirm",
          message: response.data.message || "Update wallet successfully!",
          type: "success",
        });
      }
      refetchList();
      onUnsavedChangesOk();
      closeModal();
    },
    onError: (error: { message: string }) => {
      if (openNotificationWallet) {
        openNotificationWallet({
          key: "update_wallet_failure",
          message: error?.message || "Update wallet failed!",
          type: "error",
        });
      }
    },
  });

  const [loadedFormValues, setLoadedFormValues] = useState({});

  const { mutate: deleteWallet, isLoading: loadingDeleteWallet } =
    useDeleteWallet({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotificationWallet) {
          openNotificationWallet?.({
            key: "success_transaction_confirm",
            message: response.data.message || "Delete wallet successfully!",
            type: "success",
          });
        }
        changeEditingId(undefined);
        closeModal();
        refetchList();
      },
      onError: (error: { message: string }) => {
        if (openNotificationWallet) {
          openNotificationWallet({
            key: "delete_wallet_failure",
            message: error?.message || "Delete wallet failed!",
            type: "error",
          });
        }
      },
    });

  const onOkDelete = useCallback(async () => {
    if (typeof editingId === "number") {
      await deleteWallet(editingId);
    }
  }, [deleteWallet, editingId]);

  const { openNotification, contextHolder: deleteContextHolder } =
    useNotificationOkCancel({
      ...confirmModalDeleteProps,
      onOk: onOkDelete,
    });

  const formIsLoading = loadIsLoadingDetail || isLoadingUpdate;

  const clearEditingId = useCallback(() => {
    changeEditingId(undefined);
  }, [changeEditingId]);

  useEffect(() => {
    if (!data || !loadData) return;
    const currencyData = loadData?.currencyData?.find(
      (item: any) => item.id === data?.data?.data?.assetConfigGroupId
    );
    setCurrencyData(currencyData);

    const networkData = loadData?.networkData?.find(
      (item: any) => item.id === data?.data?.data?.networkId
    );
    setNetworkData(networkData);

    const companyData = loadData?.companyData;
    setCompanyData(companyData);

    if (data) {
      formProps?.form?.setFieldsValue({
        name: data?.data?.data?.name,
        address: data?.data?.data?.address,
        currency: currencyData.symbol,
        network: networkData?.name,
        company: companyData?.name,
      });
      setLoadedFormValues(formProps?.form?.getFieldsValue());
    }
  }, [data, formProps?.form, loadData, closeModal]);

  useEffect(() => {
    if (editingId !== undefined) {
      show();
    } else {
      closeModal();
    }
  }, [editingId, closeModal, show]);

  const changeCurrencyData = useCallback(
    (data: ICurrencyToken) => {
      if (data.id !== currencyData?.id) {
        setCurrencyData(data);
      }
    },
    [currencyData?.id]
  );

  const onSelectCurrency = useCallback(
    (_: ICurrencyToken, { data }: Option<ICurrencyToken>) => {
      return changeCurrencyData(data);
    },
    [changeCurrencyData]
  );

  const clearCurrencyData = useCallback(() => {
    setCurrencyData(undefined);
  }, []);

  const changeNetworkData = useCallback(
    (data: INetwork) => {
      if (data.id !== networkData?.id) {
        setNetworkData(data);
      }
    },
    [networkData?.id]
  );

  const onSelectNetwork = useCallback(
    (_: INetwork, { data }: Option<INetwork>) => {
      return changeNetworkData(data);
    },
    [changeNetworkData]
  );

  const clearNetworkData = useCallback(() => {
    setNetworkData(undefined);
  }, []);

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      if (data.id !== companyData?.id) {
        setCompanyData(data);
      }
    },
    [companyData?.id]
  );

  const onSelectCompany = useCallback(
    (_: ICompany, { data }: Option<ICompany>) => {
      return changeCompanyData(data);
    },
    [changeCompanyData]
  );

  const clearCompanyData = useCallback(() => {
    setCompanyData(undefined);
  }, []);

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields();
    clearEditingId();
  }, [formProps.form, clearEditingId]);

  const onSubmit = (formValues: IWalletBody) => {
    updateWallet({
      id: Number(editingId),
      companyId: Number(companyData?.id),
      name: formValues?.name,
      assetConfigGroupId: currencyData?.id ? Number(currencyData.id) : "",
      networkId: Number(networkData?.id) || null,
      address: formValues.address || "",
      userId: userId || null,
    });
  };

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
  });

  const onModalCancel = useCallback(() => {
    const isFormChanged = formProps
      ? !isEqual(formProps.form?.getFieldsValue(), loadedFormValues)
      : false;
    if (isFormChanged) {
      openUnsavedChangesNotification();
    } else {
      onUnsavedChangesOk();
    }
  }, [
    formProps,
    loadedFormValues,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ]);

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps.form]);

  const Title = useCallback(
    () => (
      <>
        <p style={{ marginBottom: "0px" }}>Edit wallet information</p>
        <p>#{editingId}</p>
      </>
    ),
    [editingId]
  );

  if (editingId === undefined) {
    return null;
  }

  return (
    <>
      <Modal
        {...modalProps}
        title={<Title />}
        className="smartModalTitle"
        onCancel={onModalCancel}
        footer={ButtonListFooterWithDeleteModalSM(
          onSave,
          onModalCancel,
          openNotification,
          formIsLoading
        )}
      >
        {unsavedChangesContextHolder}
        {deleteContextHolder}
        <Form
          {...formProps}
          layout="vertical"
          onFinish={onSubmit}
          disabled={formIsLoading}
        >
          <Form.Item
            name="currency"
            rules={[
              {
                required: true,
                message: `Please select currency`,
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoCompleteCurrencyToken
              resource={`api/v1/asset-config-groups/crypto`}
              mapItem={mapCurrencyToken}
              onSelect={onSelectCurrency}
              allowClear
              refetchOnEmptyValue
              onClear={clearCurrencyData}
              params={networkData?.id}
            >
              <Input placeholder="Currency" size="large" suffix={<DownOutlined />} />
            </AutoCompleteCurrencyToken>
          </Form.Item>
          <Form.Item
            name="network"
            rules={[
              {
                required: true,
                message: `Please select network`,
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoCompleteNetwork
              resource="api/v1/networks"
              mapItem={mapNetwork}
              onSelect={onSelectNetwork}
              allowClear
              refetchOnEmptyValue
              onClear={clearNetworkData}
              params={currencyData?.id}
            >
              <Input placeholder="Network" size="large" suffix={<DownOutlined />} />
            </AutoCompleteNetwork>
          </Form.Item>
          <Form.Item name="address" rules={addressWalletRules}>
            <Input
              placeholder="Address Wallet"
              size="large"
              style={{
                height: "35px",
                fontSize: "14px",
              }}
            />
          </Form.Item>
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
              {
                min: 3,
                message: "Minimum 3 characters",
                validateTrigger: "onChange",
              },
              {
                max: 120,
                message: "Maximum 120 characters",
                validateTrigger: "onChange",
              },
            ]}
          >
            <Input
              disabled
              placeholder="Wallet name"
              size="large"
              style={{
                height: "35px",
                fontSize: "14px",
              }}
            />
          </Form.Item>
          <Form.Item
            name="company"
            rules={[
              {
                required: true,
                message: `Please select company`,
                validateTrigger: "onSubmit",
              },
            ]}
          >
            <AutoComplete
              resource={`api/v1/companies`}
              mapItem={mapCompanyItem}
              onSelect={onSelectCompany}
              additionalFilters={[companySearchFieldNameFilter]}
              allowClear
              refetchOnEmptyValue
              onClear={clearCompanyData}
              disabled={true}
            >
              <Input placeholder="Provider" size="large" suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
